import {
  ChromeClient as ChromeSdkClient,
  Domain as ChromeDomain,
  Region as ChromeRegion,
  ChromeType,
  Locale,
} from "@amzn/awsmp-chrome-java-script-client";
import { serializeError } from "serialize-error";

// CSP directives type definition
type DirectivesObj = Record<string, string[] | undefined>;

export const CHROME_DOMAIN = ChromeDomain.Prod;
export const CHROME_REGION = ChromeRegion.IAD;

const chromeSdkClient = new ChromeSdkClient({
  chromeType: ChromeType.Buyer,
  domain: CHROME_DOMAIN,
  region: CHROME_REGION,
});

// Retry options for chrome client requests
const RETRY_OPTIONS = {
  retries: 3,
  factor: 2,
  minTimeout: 1000,
  maxTimeout: 5000,
};

// Simple retry implementation with exponential backoff
async function retryWithBackoff<T>(fn: (attemptCount: number) => Promise<T>, options = RETRY_OPTIONS): Promise<T> {
  let lastError: Error | undefined;

  for (let attempt = 1; attempt <= options.retries + 1; attempt++) {
    try {
      return await fn(attempt);
    } catch (error) {
      lastError = error as Error;

      // If this is the last attempt, throw the error
      if (attempt > options.retries) {
        throw lastError;
      }

      // Calculate backoff delay with exponential increase
      const delay = Math.min(options.minTimeout * Math.pow(options.factor, attempt - 1), options.maxTimeout);

      // Wait before retrying
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}

// Simple logger interface for chrome client
const logger = {
  error: (message: string, data?: any) => {
    console.error(`[ChromeClient] ${message}`, data);
  },
  info: (message: string, data?: any) => {
    console.info(`[ChromeClient] ${message}`, data);
  },
};

// Simple metrics interface for chrome client
const metrics = {
  opsMetric: (data: { metricName: string; dimensions: Record<string, string>; unit: string; value: number }) => {
    // In a real implementation, this would send metrics to your monitoring system
    console.debug("[ChromeClient Metrics]", data);
  },
};

export interface ChromeResponse {
  [key: string]: any;
  header?: string;
  footer?: string;
  scripts?: string;
  meta?: string;
  csp: DirectivesObj;
}

export const fetchBuyerChrome = async function (locale: string = "en-US"): Promise<ChromeResponse> {
  try {
    // Convert browser locale (e.g., "en-US") to chrome locale (e.g., "en")
    const chromeLocale = locale.split("-")[0];

    // Validate the locale is supported
    if (!Object.values(Locale).includes(chromeLocale as Locale)) {
      throw new Error(`Unsupported locale provided to chrome, ${locale} (converted to ${chromeLocale})`);
    }

    const chromeResult = await retryWithBackoff(async (attemptCount: number) => {
      try {
        return await chromeSdkClient.getChrome({
          locale: chromeLocale as Locale,
        });
      } catch (err) {
        throw new Error(`Failed to fetch chrome, attempt ${attemptCount}${(err as any)?.message ? `, due to ${(err as any)?.message}` : ""}`);
      }
    });

    if (!chromeResult) {
      throw new Error("ChromeService response is undefined");
    }

    metrics.opsMetric({
      metricName: "CHROME_SERVICE_GET_CHROME_SUCCESS",
      dimensions: {
        LOCATION: typeof window !== "undefined" ? "client" : "server",
        API: "ChromeService",
        OPERATION: "GetChrome",
        REGION: CHROME_REGION,
      },
      unit: "Count",
      value: 1,
    });

    const csp = Object.fromEntries(
      Object.entries(chromeResult.csp).map(([key, values]) => [
        key,
        // Strip quotes from values, as quotes are automatically applied by csp-typed-directives.
        values?.map((value) => value.replace(/'/g, "")),
      ]),
    ) as DirectivesObj;

    return {
      ...chromeResult,
      csp,
    };
  } catch (error) {
    logger.error("Error when fetching BuyerChrome", { error: serializeError(error) });
    metrics.opsMetric({
      metricName: "CHROME_SERVICE_GET_CHROME_ERROR",
      dimensions: {
        LOCATION: typeof window !== "undefined" ? "client" : "server",
        API: "ChromeService",
        OPERATION: "GetChrome",
        REGION: CHROME_REGION,
      },
      unit: "Count",
      value: 1,
    });
    throw error;
  }
};

/**
 * ChromeClient - Handles initialization and injection of AWS Marketplace chrome
 */
export class ChromeClient {
  private locale: string;
  private headerElement: HTMLElement | null = null;
  private footerElement: HTMLElement | null = null;
  private initPromise: Promise<void>;

  constructor(locale: string = navigator.language || "en-US") {
    this.locale = locale;
    // Auto-initialize on construction and store the promise
    this.initPromise = this.initialize();
  }

  /**
   * Wait for chrome initialization to complete
   * This ensures window.awsmpue is available before continuing
   */
  public waitForInitialization(): Promise<void> {
    return this.initPromise;
  }

  /**
   * Get the current footer height
   */
  getFooterHeight(): number {
    return this.footerElement?.offsetHeight ?? 0;
  }

  /**
   * Get the current header height
   */
  getHeaderHeight(): number {
    return this.headerElement?.offsetHeight ?? 0;
  }

  /**
   * Initialize chrome by fetching content and injecting into DOM
   */
  private async initialize(): Promise<void> {
    try {
      console.log("[Chrome Init] Starting chrome initialization...");

      // Fetch chrome content
      const chromeResponse = await fetchBuyerChrome(this.locale);

      // Get chrome placeholder elements
      this.headerElement = document.getElementById("chrome-header");
      this.footerElement = document.getElementById("chrome-footer");

      if (!this.headerElement || !this.footerElement) {
        console.warn("[Chrome Init] Chrome placeholder elements not found");
        return;
      }

      // Inject header and footer HTML
      this.injectHeader(this.headerElement, chromeResponse.header);
      this.injectFooter(this.footerElement, chromeResponse.footer);

      // Inject meta tags and scripts first
      this.injectMeta(chromeResponse.meta);
      this.injectScripts(chromeResponse.scripts);

      // Wait for window.awsmpue to become available after scripts load
      await this.waitForAwsmpue();

      // Wait for stylesheets to load
      await this.waitForStylesheets();

      // Wait for styles to be applied
      await this.waitForStyleApplication();

      // Make chrome visible
      this.showChrome(this.headerElement, this.footerElement, chromeResponse);

      console.log("[Chrome Init] Chrome initialization completed successfully");
    } catch (error) {
      console.warn("[Chrome Init] Failed to initialize chrome, continuing without chrome:", error);
      this.hideChrome();
    }
  }

  /**
   * Extract HTML elements from an HTML string and create new DOM elements.
   * This is necessary because DOMParser makes script tags non-executable when using innerHTML.
   * Creating new elements programmatically allows scripts to execute when appended to the DOM.
   *
   * @param htmlString - The HTML string to parse
   * @param elementType - The type of element to extract (e.g., 'script', 'link', 'meta')
   * @returns Array of newly created HTML elements
   */
  private extractHtmlElements(htmlString: string, elementType: string): HTMLElement[] {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");

    const htmlElements = doc.head.querySelectorAll(elementType);
    const elements: HTMLElement[] = [];

    htmlElements.forEach((element) => {
      const newElement = document.createElement(elementType);
      for (const attr of element.attributes) {
        newElement.setAttribute(attr.name, attr.value);
      }
      elements.push(newElement);
    });

    return elements;
  }

  private injectMeta(meta?: string): void {
    if (meta) {
      const metaElement = document.getElementById("chrome-meta");
      if (metaElement) {
        metaElement.innerHTML = meta;
        console.log("[Chrome Init] Meta tags injected successfully");
      } else {
        console.warn("[Chrome Init] Chrome meta element not found");
      }
    }
  }

  private injectScripts(scripts?: string): void {
    if (!scripts) return;

    const scriptsElement = document.getElementById("chrome-scripts");
    if (!scriptsElement) {
      console.warn("[Chrome Init] Chrome scripts element not found");
      return;
    }

    // Clear any existing scripts before injecting new ones
    scriptsElement.innerHTML = "";

    // Extract and append scripts programmatically to ensure they execute
    this.extractHtmlElements(scripts, "script").forEach((script) => {
      scriptsElement.appendChild(script);
    });

    console.log("[Chrome Init] Scripts injected successfully");
  }

  private injectHeader(element: HTMLElement, header?: string): void {
    if (header) {
      element.innerHTML = header;
      console.log("[Chrome Init] Header injected successfully");
    }
  }

  private injectFooter(element: HTMLElement, footer?: string): void {
    if (footer) {
      element.innerHTML = footer;
      console.log("[Chrome Init] Footer injected successfully");
    }
  }

  private async waitForStylesheets(): Promise<void> {
    const allLinks = document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]');
    const chromeLinks = Array.from(allLinks).filter(
      (link) => link.href && (link.href.includes("chrome") || link.href.includes("awsstatic") || link.href.includes("marketplace")),
    );

    if (chromeLinks.length > 0) {
      console.log(`[Chrome Init] Waiting for ${chromeLinks.length} stylesheets to load`);
      await Promise.all(
        chromeLinks.map(
          (link) =>
            new Promise<void>((resolve) => {
              if (link.sheet) {
                resolve(); // Already loaded
              } else {
                link.addEventListener("load", () => resolve());
                link.addEventListener("error", () => resolve()); // Continue even on error
                // Timeout after 2 seconds
                setTimeout(() => resolve(), 2000);
              }
            }),
        ),
      );
      console.log("[Chrome Init] Stylesheets loaded");
    }
  }

  private async waitForStyleApplication(): Promise<void> {
    // Wait for next animation frame to ensure styles are applied
    await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
  }

  private showChrome(headerElement: HTMLElement, footerElement: HTMLElement, chromeResponse: ChromeResponse): void {
    if (chromeResponse.header) {
      headerElement.style.opacity = "1";
    }
    if (chromeResponse.footer) {
      footerElement.style.opacity = "1";
    }
    console.log("[Chrome Init] Chrome displayed");
  }

  private hideChrome(): void {
    const headerElement = document.getElementById("chrome-header");
    const footerElement = document.getElementById("chrome-footer");

    if (headerElement) {
      headerElement.style.display = "none";
    }
    if (footerElement) {
      footerElement.style.display = "none";
    }
  }

  /**
   * Poll for window.awsmpue to become available after scripts are injected
   */
  private async waitForAwsmpue(timeout: number = 5000): Promise<void> {
    const startTime = Date.now();
    const pollInterval = 50; // Check every 50ms

    while (!(window as any).awsmpue) {
      if (Date.now() - startTime > timeout) {
        console.warn("[Chrome Init] Timeout waiting for window.awsmpue, continuing without official logger");
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
    }

    console.log("[Chrome Init] window.awsmpue is now available", window.awsmpue);
  }
}
